<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import SettingsLayout from '../../SettingsLayout.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SHEETS_TEMPLATE from './sheetsTemplate.js';

// One page, two guides (route param): webhooks (Zapier / Make / n8n) and the
// Google Sheets export with its ready Apps Script template.
const route = useRoute();
const { t, tm, rt } = useI18n();

const guide = computed(() =>
  route.params.guide === 'sheets' ? 'SHEETS' : 'WEBHOOKS'
);
const K = computed(() => `INTEGRATION_SETTINGS.TELETEL_GUIDES.${guide.value}`);
const list = key => (tm(`${K.value}.${key}`) || []).map(item => rt(item));
const eventKeys = [
  'conversation_created',
  'conversation_status_changed',
  'conversation_updated',
  'message_created',
  'message_updated',
  'contact_created',
  'contact_updated',
  'webwidget_triggered',
];
const tools = ['ZAPIER', 'MAKE', 'N8N'];

// Abridged real shape of a conversation_created payload, for mapping fields.
const PAYLOAD_EXAMPLE = `{
  "event": "conversation_created",
  "id": 28117,
  "status": "open",
  "inbox": { "id": 73, "name": "واتساب 5557" },
  "meta": {
    "sender": {
      "id": 9021,
      "name": "أحمد",
      "phone_number": "+9665xxxxxxx",
      "email": null
    },
    "assignee": null
  },
  "messages": [{ "content": "السلام عليكم، أريد حجز موعد" }],
  "custom_attributes": {},
  "labels": []
}`;

const copied = ref('');
const copy = async (text, key) => {
  await copyTextToClipboard(text);
  copied.value = key;
  setTimeout(() => {
    copied.value = '';
  }, 1500);
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="t(`${K}.TITLE`)"
        :description="t(`${K}.INTRO`)"
        :back-button-label="t('INTEGRATION_SETTINGS.TELETEL_GUIDES.BACK')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 max-w-3xl text-n-slate-12">
        <!-- Webhooks guide -->
        <template v-if="guide === 'WEBHOOKS'">
          <section>
            <h2 class="text-heading-3 mb-2">{{ t(`${K}.STEPS_TITLE`) }}</h2>
            <ol
              class="list-decimal ltr:pl-5 rtl:pr-5 flex flex-col gap-1 text-body-main"
            >
              <li v-for="(step, i) in list('STEPS')" :key="i">{{ step }}</li>
            </ol>
            <router-link
              :to="{ name: 'settings_integrations_webhook' }"
              class="inline-block mt-3"
            >
              <Button
                :label="t(`${K}.GO_WEBHOOKS`)"
                icon="i-lucide-webhook"
                sm
              />
            </router-link>
          </section>

          <section>
            <h2 class="text-heading-3 mb-2">{{ t(`${K}.EVENTS_TITLE`) }}</h2>
            <dl
              class="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-body-main"
            >
              <template v-for="event in eventKeys" :key="event">
                <dt>
                  <code
                    class="text-xs bg-n-alpha-2 rounded px-1.5 py-0.5"
                    dir="ltr"
                    >{{ event }}</code
                  >
                </dt>
                <dd class="mb-0 text-n-slate-11">
                  {{ t(`${K}.EVENTS.${event}`) }}
                </dd>
              </template>
            </dl>
          </section>

          <section class="flex flex-col gap-4">
            <h2 class="text-heading-3 mb-0">{{ t(`${K}.TOOLS_TITLE`) }}</h2>
            <div
              v-for="tool in tools"
              :key="tool"
              class="p-4 rounded-xl outline outline-1 outline-n-container bg-n-card"
            >
              <h3 class="text-sm font-semibold mb-2">
                {{ t(`${K}.TOOLS.${tool}.NAME`) }}
              </h3>
              <ol
                class="list-decimal ltr:pl-5 rtl:pr-5 flex flex-col gap-1 text-body-main mb-0"
              >
                <li v-for="(step, i) in list(`TOOLS.${tool}.STEPS`)" :key="i">
                  {{ step }}
                </li>
              </ol>
            </div>
          </section>

          <section>
            <div class="flex items-center justify-between mb-2">
              <h2 class="text-heading-3 mb-0">{{ t(`${K}.PAYLOAD_TITLE`) }}</h2>
              <Button
                :label="
                  copied === 'payload'
                    ? t('INTEGRATION_SETTINGS.TELETEL_GUIDES.COPIED')
                    : t('INTEGRATION_SETTINGS.TELETEL_GUIDES.COPY')
                "
                icon="i-lucide-copy"
                slate
                faded
                xs
                @click="copy(PAYLOAD_EXAMPLE, 'payload')"
              />
            </div>
            <pre
              dir="ltr"
              class="text-xs leading-5 p-3 rounded-xl bg-n-alpha-2 text-n-slate-12 overflow-x-auto whitespace-pre"
              >{{ PAYLOAD_EXAMPLE }}</pre
            >
          </section>

          <section>
            <h2 class="text-heading-3 mb-2">{{ t(`${K}.SECURITY_TITLE`) }}</h2>
            <p class="text-body-main text-n-slate-11 mb-0">
              {{ t(`${K}.SECURITY`) }}
            </p>
          </section>
        </template>

        <!-- Google Sheets guide -->
        <template v-else>
          <section>
            <h2 class="text-heading-3 mb-2">{{ t(`${K}.OPTION_A_TITLE`) }}</h2>
            <ol
              class="list-decimal ltr:pl-5 rtl:pr-5 flex flex-col gap-1 text-body-main mb-0"
            >
              <li v-for="(step, i) in list('OPTION_A_STEPS')" :key="i">
                {{ step }}
              </li>
            </ol>
            <router-link
              :to="{
                name: 'settings_integrations_teletel_guide',
                params: { guide: 'webhooks' },
              }"
              class="inline-block mt-3"
            >
              <Button
                :label="
                  t('INTEGRATION_SETTINGS.TELETEL_GUIDES.CARDS.WEBHOOKS.ACTION')
                "
                icon="i-lucide-webhook"
                slate
                faded
                sm
              />
            </router-link>
          </section>

          <section>
            <h2 class="text-heading-3 mb-2">{{ t(`${K}.OPTION_B_TITLE`) }}</h2>
            <ol
              class="list-decimal ltr:pl-5 rtl:pr-5 flex flex-col gap-1 text-body-main mb-0"
            >
              <li v-for="(step, i) in list('OPTION_B_STEPS')" :key="i">
                {{ step }}
              </li>
            </ol>
          </section>

          <section>
            <div class="flex items-center justify-between mb-2">
              <h2 class="text-heading-3 mb-0">
                {{ t(`${K}.TEMPLATE_TITLE`) }}
              </h2>
              <Button
                :label="
                  copied === 'template'
                    ? t('INTEGRATION_SETTINGS.TELETEL_GUIDES.COPIED')
                    : t('INTEGRATION_SETTINGS.TELETEL_GUIDES.COPY')
                "
                icon="i-lucide-copy"
                sm
                @click="copy(SHEETS_TEMPLATE, 'template')"
              />
            </div>
            <pre
              dir="ltr"
              class="text-xs leading-5 p-3 rounded-xl bg-n-alpha-2 text-n-slate-12 overflow-x-auto whitespace-pre"
              >{{ SHEETS_TEMPLATE }}</pre
            >
            <p class="text-body-main text-n-slate-11 mt-2 mb-0">
              <span class="font-medium text-n-slate-12"
                >{{ t(`${K}.COLUMNS_TITLE`) }}:</span
              >
              {{ t(`${K}.COLUMNS`) }}
            </p>
          </section>

          <section>
            <h2 class="text-heading-3 mb-2">{{ t(`${K}.NOTES_TITLE`) }}</h2>
            <ul
              class="list-disc ltr:pl-5 rtl:pr-5 flex flex-col gap-1 text-body-main text-n-slate-11 mb-0"
            >
              <li v-for="(note, i) in list('NOTES')" :key="i">{{ note }}</li>
            </ul>
          </section>
        </template>
      </div>
    </template>
  </SettingsLayout>
</template>
